// Soft UI Dashboard React Base Styles
import colors from "./colors";

// Soft UI Dashboard React Helper Functions
import pxToRem from "../functions/pxToRem";

const { dark } = colors;

type basePropertiesType = {
  fontFamily: string,
  fontWeightLight: number,
  fontWeightRegular: number,
  fontWeightMedium: number,
  fontWeightBold: number,
  fontSizeXXS: string,
  fontSizeXS: string,
  fontSizeSM: string,
  fontSizeMD: string,
  fontSizeLG: string,
  fontSizeXL: string,
};

const baseProperties: basePropertiesType = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
};



type BaseHeadingPropertiesType = {
  fontFamily: string,
  color: string,
  fontWeight: number,
}

const baseHeadingProperties: BaseHeadingPropertiesType = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightMedium,
};



type BaseDisplayPropertiesType = {
  fontFamily: string,
  color: string,
  fontWeight: number,
  lineHeight: number,
};

const baseDisplayProperties: BaseDisplayPropertiesType = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
};



type TypographyType = {
  fontFamily: string,
  fontWeightLight: number,
  fontWeightRegular: number,
  fontWeightMedium: number,
  fontWeightBold: number,

  h1: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  h2: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  h3: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  h4: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  h5: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  h6: {
    fontSize: string,
    lineHeight: number,

  } & BaseHeadingPropertiesType,

  subtitle1: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
  },

  subtitle2: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
  },

  body1: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
  },

  body2: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
  },

  button: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
    textTransform: "uppercase",
  },

  caption: {
    fontFamily: string,
    fontSize: string,
    fontWeight: number,
    lineHeight: number,
  },

  overline: {
    fontFamily: string,
  },

  d1: {
    fontSize: string,
  } & BaseDisplayPropertiesType,

  d2: {
    fontSize: string,

  } & BaseDisplayPropertiesType,

  d3: {
    fontSize: string,

  } & BaseDisplayPropertiesType,

  d4: {
    fontSize: string,

  } & BaseDisplayPropertiesType,

  d5: {
    fontSize: string,

  } & BaseDisplayPropertiesType,

  d6: {
    fontSize: string,

  } & BaseDisplayPropertiesType,

  size: {
    xxs: string,
    xs: string,
    sm: string,
    md: string,
    lg: string,
    xl: string,
  },

  lineHeight: {
    sm: number,
    md: number,
    lg: number,
  },
};

const typography: TypographyType = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontSize: pxToRem(48),
    lineHeight: 1.25,
    ...baseHeadingProperties,
  },

  h2: {
    fontSize: pxToRem(36),
    lineHeight: 1.3,
    ...baseHeadingProperties,
  },

  h3: {
    fontSize: pxToRem(30),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h4: {
    fontSize: pxToRem(24),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h5: {
    fontSize: pxToRem(20),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h6: {
    fontSize: pxToRem(16),
    lineHeight: 1.625,
    ...baseHeadingProperties,
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.6,
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
  },

  body2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.6,
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightBold,
    lineHeight: 1.5,
    textTransform: "uppercase",
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.25,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
};

export default typography;
